import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft, Link as LinkIcon, MapPin } from 'lucide-react'
import {
  PiIdentificationCard,
  PiHandshake,
  PiNotepad,
  PiCalendarStar,
  PiFileText,
  PiCirclesThreePlus,
} from 'react-icons/pi'
import { toast } from 'react-toastify'
import apiService from 'services/api'
import LoadingScreen from 'components/Layout/LoadingScreen'
import { MobileColors } from 'styles/mobileTokens'
import { formatDate2 } from 'utils/formatting'
import { MobileAccountAttributesSummary } from 'components/MobileCompanyPage/MobileAccountAttributes'
import { MobileDealsAccordion } from 'components/MobileCompanyPage/MobileDealsAccordion'
import { MobileEventHistoryAccordion } from 'components/MobileCompanyPage/MobileEventHistory'
import { MobileRemindersAccordion } from 'components/MobileCompanyPage/MobileReminders'
import { CustomCompanyDataDrawer } from 'components/MobileCompanyPage/CustomCompanyDataDrawer'
import useDocumentTitle from 'components/useDocumentTitle'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { getIdWithPrefix } from 'utils/pathUtils'
import { ContactCompanyType } from 'models/contact_companies'
import { BackButton } from 'components/Mobile/Mobile.styles'

// Styled Components
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${MobileColors.backgroundWhite};
  overflow-y: auto;
  padding-bottom: 200px;
`

const CompanyHeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const CustomLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: ${MobileColors.textSecondary};
  text-transform: uppercase;
  margin-bottom: 4px;

  svg {
    font-size: 14px;
  }
`

const CompanyName = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${MobileColors.textPrimary};
  margin: 0 0 8px 0;
`

const CompanyMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${MobileColors.textSecondary};
`

const MetaDivider = styled.span`
  color: ${MobileColors.borderDefault};
`

const ActionButtonsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  padding: 12px 10px;
  background: ${MobileColors.primary};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const ActionButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px;
  background: ${MobileColors.primaryActive};
  border: none;
  border-radius: 8px;
  margin: 3px;
  color: #ffffff;
  font-size: 9px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  svg {
    font-size: 20px;
    color: #ffffff;
  }

  &:hover {
    background: ${MobileColors.primary};
  }

  &:active {
    background: ${MobileColors.primaryDark};
  }
`

const ActionCount = styled.span`
  font-size: 11px;
  font-weight: 300;
`

const ContentSection = styled.div`
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
`

const SectionDivider = styled.div`
  height: 8px;
  background: ${MobileColors.backgroundOverlay};
`

const ChainLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${MobileColors.primary};
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${MobileColors.textSecondary};
  transition: color 0.2s;

  &:hover {
    color: ${MobileColors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

export function MobileCustomDoorPage() {
  const navigate = useNavigate()
  const urlParams = useParams()
  const { preferences } = usePreferences()

  const doorProxyID = urlParams?.door_proxy_id
    ? getIdWithPrefix(
        urlParams.door_proxy_id,
        preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated]
      )
    : undefined

  const api = apiService()
  const { data: doorProxyData, isLoading: doorLoading } =
    api.useGetDoorProxy(doorProxyID)

  // Fetch parent company data
  const { data: companyData, isLoading: companyLoading } = api.useGetCompany(
    doorProxyData?.chain_proxy || 0
  )

  const isLoading = doorLoading || companyLoading

  // Set document title
  useDocumentTitle(`${doorProxyData?.name || 'Not Found'} (Door) - First Bite`)

  // Build full address
  const getFullAddress = () => {
    const parts = [
      doorProxyData?.address,
      doorProxyData?.city,
      doorProxyData?.state,
      doorProxyData?.zipcode,
      doorProxyData?.country,
    ].filter(Boolean)
    return parts.join(', ')
  }

  // Navigate to parent company
  const handleParentCompanyClick = () => {
    if (companyData?.id) {
      const companyPrefix =
        preferences?.company_type_prefix_map[
          ContactCompanyType.UserGenerated
        ] || ''
      navigate(`/company/${companyPrefix}${companyData.id}`)
    }
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!doorProxyData) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">Door not found</p>
        </div>
      </PageContainer>
    )
  }

  // For door pages, chainProxyId is the parent company id
  const chainProxyId = doorProxyData.chain_proxy
  const companyPrefix =
    preferences?.company_type_prefix_map[ContactCompanyType.UserGenerated] || ''

  return (
    <>
      <PageContainer>
        {/* Back Button */}
        <BackButton onClick={() => navigate(-1)}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>

        {/* Door Header */}
        <CompanyHeaderSection>
          {/* Custom Label */}
          <CustomLabel>
            <PiCirclesThreePlus />
            CUSTOM
          </CustomLabel>

          {/* Door Name */}
          <CompanyName>{doorProxyData.name}</CompanyName>

          {/* Meta Row: Address, Created Date, Social Icons */}
          <CompanyMetaRow>
            {getFullAddress() && (
              <span className="flex items-center gap-1">
                <MapPin size={12} />
                {getFullAddress()}
              </span>
            )}
            {doorProxyData.created && (
              <>
                <MetaDivider>•</MetaDivider>
                <span>Created {formatDate2(doorProxyData.created)}</span>
              </>
            )}
            {doorProxyData.domain && (
              <>
                <MetaDivider>•</MetaDivider>
                <SocialLink
                  href={
                    doorProxyData.domain.startsWith('http')
                      ? doorProxyData.domain
                      : `https://${doorProxyData.domain}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Visit website"
                >
                  <LinkIcon />
                </SocialLink>
              </>
            )}
          </CompanyMetaRow>

          {/* Part of [Company Name] Link */}
          {companyData && (
            <ChainLinkContainer onClick={handleParentCompanyClick}>
              <span>Part of {companyData.name}</span>
              <span className="text-xs">View Company →</span>
            </ChainLinkContainer>
          )}
        </CompanyHeaderSection>

        {/* Action Buttons (5 buttons, matching MobileDoorPage layout) */}
        <ActionButtonsRow>
          <ActionButton
            onClick={() => navigate(`/deals?door_proxy_id=${doorProxyData.id}`)}
          >
            <PiHandshake />
            <ActionCount>({doorProxyData.deal_count || 0})</ActionCount>
          </ActionButton>

          <ActionButton
            onClick={() =>
              navigate(`/contacts?door_proxy_id=${doorProxyData.id}`)
            }
          >
            <PiIdentificationCard />
            <ActionCount>({doorProxyData.contact_count || 0})</ActionCount>
          </ActionButton>

          <ActionButton onClick={() => toast.info('Samples - Coming soon')}>
            <PiFileText />
            <ActionCount>(0)</ActionCount>
          </ActionButton>

          <ActionButton
            onClick={() =>
              navigate(
                `/notes?door_proxy_id=${doorProxyData.id}&door_name=${encodeURIComponent(
                  doorProxyData.name || ''
                )}&contact_company_id=${chainProxyId || ''}&company_name=${encodeURIComponent(
                  companyData?.name || ''
                )}`
              )
            }
          >
            <PiNotepad />
            <ActionCount>({doorProxyData.note_count || 0})</ActionCount>
          </ActionButton>

          <ActionButton
            onClick={() =>
              navigate(
                `/reminders?door_id=${doorProxyData.id}&door_name=${encodeURIComponent(
                  doorProxyData.name || ''
                )}`
              )
            }
          >
            <PiCalendarStar />
            <ActionCount>(0)</ActionCount>
          </ActionButton>
        </ActionButtonsRow>

        {/* Account Attributes Section */}
        <ContentSection>
          <MobileAccountAttributesSummary
            chainProxyId={chainProxyId}
            chainName={`${companyPrefix}${chainProxyId}`}
            doorProxyId={doorProxyData.id}
          />
        </ContentSection>

        <SectionDivider />

        {/* Deals Overview Section */}
        <ContentSection>
          <MobileDealsAccordion
            chainProxyId={chainProxyId}
            doorProxyId={doorProxyData.id}
          />
        </ContentSection>

        <SectionDivider />

        {/* Reminders Section */}
        <ContentSection>
          <MobileRemindersAccordion
            chainProxyId={chainProxyId}
            doorProxyId={doorProxyData.id}
          />
        </ContentSection>

        <SectionDivider />

        {/* Event History Section */}
        <ContentSection>
          <MobileEventHistoryAccordion
            chainProxyId={chainProxyId}
            doorProxyId={doorProxyData.id}
          />
        </ContentSection>
      </PageContainer>

      {/* Custom Company Data Drawer */}
      <CustomCompanyDataDrawer
        chainProxyId={chainProxyId}
        doorProxyId={doorProxyData.id}
        companyName={companyData?.name}
        doorName={doorProxyData.name}
        doorAddress={getFullAddress()}
        doorPhone={doorProxyData.phone_number}
        doorWebsite={doorProxyData.domain}
        doorCategory={companyData?.category}
        doorType={companyData?.sub_category?.name}
        createdDate={doorProxyData.created}
        modifiedDate={companyData?.modified}
      />
    </>
  )
}
